'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@algoacademy/design-system/web/actions/Button';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { isSensitiveTelemetryPath } from '@/app/_observability/sensitiveTelemetryPaths';
import { ANALYTICS_CONSENT_STORAGE_KEY, type AnalyticsConsent, getGoogleConsentState, OPEN_ANALYTICS_CONSENT_EVENT, parseAnalyticsConsent } from './analyticsConsent';
type GoogleTagWindow = Window & {
  dataLayer?: Array<Record<string, unknown>>;
  gtag?: (command: 'consent', action: 'update', consent: ReturnType<typeof getGoogleConsentState>) => void;
  __aaLoadGoogleTagManager?: () => void;
  __aaGoogleTagManagerLoaded?: boolean;
};
function readAnalyticsConsent(): AnalyticsConsent | null {
  try {
    return parseAnalyticsConsent(window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
}
function storeAnalyticsConsent(consent: AnalyticsConsent): void {
  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, consent);
  } catch {
    // The choice still applies to the current page when browser storage is unavailable.
  }
}
function deleteGoogleAnalyticsCookies(): void {
  const cookieNames = document.cookie.split(';').map(cookie => cookie.trim().split('=')[0]).filter(name => name === '_ga' || name.startsWith('_ga_'));
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domains = window.location.hostname.endsWith('algoacademy.pl') ? ['', '; Domain=.algoacademy.pl'] : [''];
  for (const name of cookieNames) {
    for (const domain of domains) {
      // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API has limited support; expiring GA cookies must work in all supported browsers.
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secure}${domain}`;
    }
  }
}
function applyAnalyticsConsent(consent: AnalyticsConsent): void {
  const googleTagWindow = window as GoogleTagWindow;
  googleTagWindow.gtag?.('consent', 'update', getGoogleConsentState(consent));
  if (consent === 'granted') {
    googleTagWindow.__aaLoadGoogleTagManager?.();
  } else {
    const shouldReloadWithoutGoogleTagManager = googleTagWindow.__aaGoogleTagManagerLoaded === true;
    deleteGoogleAnalyticsCookies();
    if (shouldReloadWithoutGoogleTagManager) window.location.reload();
  }
}
export function AnalyticsConsentBanner() {
  const $ = _c(12);
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  let t0;
  if ($[0] !== pathname) {
    t0 = isSensitiveTelemetryPath(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isSensitivePath = t0;
  let t1;
  let t2;
  if ($[2] !== isSensitivePath) {
    t1 = () => {
      const consent = readAnalyticsConsent();
      setIsOpen(consent === null && !isSensitivePath);
      if (consent === "granted" && !isSensitivePath) {
        const googleTagWindow = window as GoogleTagWindow;
        googleTagWindow.__aaLoadGoogleTagManager?.();
      }
    };
    t2 = [isSensitivePath];
    $[2] = isSensitivePath;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const openSettings = () => setIsOpen(true);
      const syncConsentFromAnotherTab = event => {
        if (event.key !== ANALYTICS_CONSENT_STORAGE_KEY) {
          return;
        }
        const consent_0 = parseAnalyticsConsent(event.newValue);
        setIsOpen(consent_0 === null);
        if (consent_0) {
          applyAnalyticsConsent(consent_0);
        }
      };
      window.addEventListener(OPEN_ANALYTICS_CONSENT_EVENT, openSettings);
      window.addEventListener("storage", syncConsentFromAnotherTab);
      return () => {
        window.removeEventListener(OPEN_ANALYTICS_CONSENT_EVENT, openSettings);
        window.removeEventListener("storage", syncConsentFromAnotherTab);
      };
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = consent_1 => {
      storeAnalyticsConsent(consent_1);
      applyAnalyticsConsent(consent_1);
      setIsOpen(false);
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const chooseConsent = t5;
  if (!isOpen || isSensitivePath) {
    return null;
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h2 className="font-semibold text-base text-slate-950 sm:text-lg">Pomóż nam ulepszać AlgoAcademy</h2>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="max-w-2xl">{t6}<p className="mt-1 text-slate-600 text-sm leading-6">Za Twoją zgodą użyjemy Google Analytics, żeby sprawdzać, co na stronie działa dobrze, a co możemy poprawić. Możesz odmówić — strona będzie działać tak samo.{" "}<NextLink href="/polityka-prywatnosci#pliki-cookies" className="font-medium text-slate-800 underline decoration-slate-400 underline-offset-2 hover:text-slate-950">Więcej informacji</NextLink>.</p></div>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Button outline={true} size="lg" className="w-full sm:w-auto" onClick={() => chooseConsent("denied")}>Nie zgadzam się</Button>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <section aria-label="Zgoda na analityczne pliki cookies" className="fixed inset-x-4 bottom-4 z-[100] mx-auto max-w-4xl rounded-xl border border-slate-200 bg-white p-4 shadow-2xl sm:p-5" data-testid="analytics-consent-banner"><div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">{t7}<div className="grid shrink-0 grid-cols-1 gap-2 sm:grid-cols-2">{t8}<Button color="algoprimary" size="lg" className="w-full sm:w-auto" onClick={() => chooseConsent("granted")}>Zgadzam się</Button></div></div></section>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
}
export function AnalyticsCookieSettingsButton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Button outline={true} size="lg" onClick={_temp}>Zmień zgodę na pliki analityczne</Button>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp() {
  return window.dispatchEvent(new Event(OPEN_ANALYTICS_CONSENT_EVENT));
}